/*
  Readers for the parts of a v8 project file beyond the roster.

  Every function here takes untrusted input — a workbook somebody uploaded — and returns something the
  domain recognises or nothing at all. Nothing throws on malformed data: a file with one garbled TSA
  should import the other nine, not fail entirely, because the alternative is a consultant unable to
  move an engagement onto the platform with no idea which cell is at fault. What is dropped is counted
  and reported instead (see ImportReport).

  Ids from the file become object keys and database keys, so every one goes through isForbiddenKey.
  `__proto__` as a personnel number is not hypothetical — the id comes from a client's spreadsheet.
*/
import { bucketMetaSchema, bucketSchema, buildUpSchema, entityClassificationSchema, filterConditionSchema, nodeDisplaySchema, pptxGeomSchema, strandedPlanEntrySchema, tsaSchema, viewGroupingSchema, } from '@integrate/contracts';
import { groupNodeId } from './hierarchy.js';
import { isForbiddenKey, parseNumber } from './project-file.js';
export function freshReport() {
    return { dropped: [] };
}
function plainObject(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return null;
    return value;
}
function str(value) {
    if (value == null)
        return null;
    const s = String(value).trim();
    return s === '' ? null : s;
}
/**
 * A month on the TSA clock, saying so when it had to shorten one.
 *
 * The ceiling used to be 36, on the argument that every timeline and month input stopped there. That
 * stopped being true when the chart axis started following the data: an agreement can run to M120, so a
 * replacement needed at M48 is now perfectly drawable and refusing it would put a number on screen that
 * contradicts the file it came from. 120 matches `durationMonths` and `agreementMonthSchema`.
 *
 * The app's own inputs are uncapped, so a file can still carry something past 120 — reported rather than
 * silently truncated, which is the whole point of this function.
 */
const MONTH_CEILING = 120;
function clampMonths(value, what, report) {
    if (value == null)
        return null;
    const n = Math.max(0, Math.round(Number(value) || 0));
    if (n <= MONTH_CEILING)
        return n;
    report.dropped.push(`${what} month ${n} shortened to the ${MONTH_CEILING}-month ceiling`);
    return MONTH_CEILING;
}
/* ------------------------------------------------------------------ *
 * Decisions
 * ------------------------------------------------------------------ */
export function bucketsFromMeta(raw, known, report) {
    const out = Object.create(null);
    const source = plainObject(raw);
    if (!source)
        return out;
    for (const [employeeId, value] of Object.entries(source)) {
        if (isForbiddenKey(employeeId))
            continue;
        if (!known.has(employeeId)) {
            report.dropped.push(`bucket for unknown person ${employeeId}`);
            continue;
        }
        const parsed = bucketSchema.safeParse(value);
        if (parsed.success)
            out[employeeId] = parsed.data;
    }
    return out;
}
export function bucketMetaFromMeta(raw, known) {
    const out = Object.create(null);
    const source = plainObject(raw);
    if (!source)
        return out;
    for (const [employeeId, value] of Object.entries(source)) {
        if (isForbiddenKey(employeeId) || !known.has(employeeId))
            continue;
        const entry = plainObject(value);
        if (!entry)
            continue;
        // Defaults rather than rejection: an old file may carry only `src`, and a carve-out member with no
        // recorded stage joins at the perimeter, which is what the app assumes on read.
        const parsed = bucketMetaSchema.safeParse({
            src: entry.src ?? 'manual',
            joinStage: entry.joinStage ?? 'perimeter',
            ...(entry.day2Exit ? { day2Exit: true } : {}),
        });
        if (parsed.success)
            out[employeeId] = parsed.data;
    }
    return out;
}
export function strandedPlanFromMeta(raw, known) {
    const out = Object.create(null);
    const source = plainObject(raw);
    if (!source)
        return out;
    for (const [employeeId, value] of Object.entries(source)) {
        if (isForbiddenKey(employeeId) || !known.has(employeeId))
            continue;
        const entry = plainObject(value);
        if (!entry)
            continue;
        const parsed = strandedPlanEntrySchema.safeParse({
            action: entry.action,
            month: Math.max(0, Math.min(36, Math.round(Number(entry.month) || 0))),
        });
        if (parsed.success)
            out[employeeId] = parsed.data;
    }
    return out;
}
/* ------------------------------------------------------------------ *
 * Heads — three legacy generations plus the current one
 * ------------------------------------------------------------------ */
/*
  Every perspective a head can be designated in — which is FOUR, not three.

  This list said three while `setHead` has always accepted `headPerspectiveSchema`, which includes
  `target`, and while the export writes `heads` whole. So a target-org head designation travelled out in
  the file and was silently dropped coming back in, on the path this format exists to make two-way.

  Kept as its own list rather than imported from contracts, because the three GENERATIONS below are about
  file shapes that predate the platform: gen 1 and gen 2 can only ever describe the as-is org, and reading
  them through a schema that knows about `target` would suggest they could carry one.
*/
const PERSPECTIVES = ['as-is', 'remain', 'carve-out', 'target'];
/**
 * Head designations, migrated across every format the file may carry.
 *
 * Ported from the restore at orgchart.html:9376. A null value is preserved deliberately: it means
 * somebody said this group has NO head, which is not the same as no designation at all.
 *
 *   Gen 1 — { function: {name: id}, subFunction: {'fn||sub': id} }, before levels were generic
 *   Gen 2 — flat { groupId: id }, before perspectives existed
 *   Gen 3 — { 'as-is': {...}, remain: {...}, 'carve-out': {...} }, the current shape
 */
export function headsFromMeta(raw) {
    const out = {
        'as-is': Object.create(null),
        remain: Object.create(null),
        'carve-out': Object.create(null),
        target: Object.create(null),
    };
    const source = plainObject(raw);
    if (!source)
        return out;
    const gen1Function = plainObject(source.function);
    const gen1Sub = plainObject(source.subFunction);
    if (gen1Function || gen1Sub) {
        for (const [value, employeeId] of Object.entries(gen1Function ?? {})) {
            if (isForbiddenKey(value))
                continue;
            out['as-is'][groupNodeId([{ levelKey: 'function', value }])] = str(employeeId);
        }
        for (const [key, employeeId] of Object.entries(gen1Sub ?? {})) {
            if (isForbiddenKey(key))
                continue;
            const [fn, sub] = key.split('||');
            out['as-is'][groupNodeId([
                { levelKey: 'function', value: fn ?? '' },
                { levelKey: 'subFunction', value: sub ?? '' },
            ])] = str(employeeId);
        }
        return out;
    }
    const byPerspective = PERSPECTIVES.some((p) => plainObject(source[p]));
    if (byPerspective) {
        for (const perspective of PERSPECTIVES) {
            for (const [groupId, employeeId] of Object.entries(plainObject(source[perspective]) ?? {})) {
                if (isForbiddenKey(groupId))
                    continue;
                // `null` survives: an explicit "no head" must not become "nobody decided".
                out[perspective][groupId] = employeeId === null ? null : str(employeeId);
            }
        }
        return out;
    }
    // Gen 2: a flat map, which predates perspectives, so it describes the as-is org.
    for (const [groupId, employeeId] of Object.entries(source)) {
        if (isForbiddenKey(groupId))
            continue;
        out['as-is'][groupId] = employeeId === null ? null : str(employeeId);
    }
    return out;
}
export function overridesFromMeta(raw) {
    const out = Object.create(null);
    const source = plainObject(raw);
    if (!source)
        return out;
    for (const [perspective, value] of Object.entries(source)) {
        if (isForbiddenKey(perspective))
            continue;
        const bundle = plainObject(value);
        if (!bundle)
            continue;
        const empOverrides = Object.create(null);
        for (const [employeeId, fields] of Object.entries(plainObject(bundle.empOverrides) ?? {})) {
            if (isForbiddenKey(employeeId))
                continue;
            const byField = plainObject(fields);
            if (!byField)
                continue;
            const clean = Object.create(null);
            for (const [fieldKey, fieldValue] of Object.entries(byField)) {
                if (isForbiddenKey(fieldKey))
                    continue;
                if (fieldValue === null || typeof fieldValue === 'string' || typeof fieldValue === 'number') {
                    clean[fieldKey] = fieldValue;
                }
            }
            if (Object.keys(clean).length)
                empOverrides[employeeId] = clean;
        }
        const groupRenames = Object.create(null);
        for (const [fieldKey, mapping] of Object.entries(plainObject(bundle.groupRenames) ?? {})) {
            if (isForbiddenKey(fieldKey))
                continue;
            const byValue = plainObject(mapping);
            if (!byValue)
                continue;
            const clean = Object.create(null);
            for (const [oldValue, newValue] of Object.entries(byValue)) {
                if (isForbiddenKey(oldValue))
                    continue;
                const next = str(newValue);
                if (next !== null)
                    clean[oldValue] = next;
            }
            if (Object.keys(clean).length)
                groupRenames[fieldKey] = clean;
        }
        out[perspective] = {
            disabledLevels: Array.isArray(bundle.disabledLevels)
                ? bundle.disabledLevels.filter((l) => typeof l === 'string')
                : [],
            empOverrides,
            groupRenames,
        };
    }
    return out;
}
/* ------------------------------------------------------------------ *
 * Registers
 * ------------------------------------------------------------------ */
export function tsasFromMeta(raw, report) {
    if (!Array.isArray(raw))
        return [];
    const out = [];
    const seen = new Set();
    for (const value of raw) {
        const entry = plainObject(value);
        if (!entry)
            continue;
        const id = str(entry.id);
        if (!id || isForbiddenKey(id) || seen.has(id))
            continue;
        const parsed = tsaSchema.safeParse({
            id,
            name: str(entry.name) ?? id,
            service: str(entry.service),
            area: str(entry.area),
            direction: entry.direction === 'provided' ? 'provided' : 'received',
            durationMonths: Math.max(0, Math.round(Number(entry.durationMonths) || 0)),
            monthlyCost: Math.max(0, Number(entry.monthlyCost) || 0),
            status: entry.status === 'agreed' || entry.status === 'exited' ? entry.status : 'planned',
            /*
              Absent in every file the frozen app writes, and that absence has to mean something exact.
      
              Null is "no month recorded", which prices an exited agreement at nothing — the behaviour this
              column replaces and the one the parity harness compares against. So a legacy file imports to
              exactly the figures it produced before the field existed, and only a month somebody typed here
              moves one. Kept whatever the status says, because it is read only when the status is 'exited'
              and clearing it would lose the date for anyone who flips a status back to check something.
            */
            exitedAtMonth: clampMonths(entry.exitedAtMonth, `TSA ${id} exit`, report),
            note: str(entry.note),
        });
        if (!parsed.success) {
            report.dropped.push(`TSA ${id}`);
            continue;
        }
        seen.add(id);
        out.push(parsed.data);
    }
    return out;
}
/**
 * Build-ups, read out of the payload's LEGACY flat shape.
 *
 * The metadata payload is a legacy-format artifact: the single-file app reads these objects directly as
 * employees, so the derived values carry underscores (`_dedication`, `_openPosition`) and the hierarchy
 * level values sit flat on the object rather than under `levels`. Reading them as the platform's own
 * shape would silently produce build-ups with no level values and no dedication.
 */
export function buildUpsFromMeta(raw, groupingLevels, report) {
    if (!Array.isArray(raw))
        return [];
    const out = [];
    const seen = new Set();
    for (const value of raw) {
        const entry = plainObject(value);
        if (!entry)
            continue;
        const id = str(entry.id);
        if (!id || isForbiddenKey(id) || seen.has(id))
            continue;
        const levels = Object.create(null);
        for (const level of groupingLevels)
            levels[level.key] = str(entry[level.key]);
        const parsed = buildUpSchema.safeParse({
            id,
            name: str(entry.name) ?? id,
            // `str` maps '' to null, which is what the app writes for "— Top level —".
            supervisorId: str(entry.supervisorId),
            jobTitle: str(entry.jobTitle),
            location: str(entry.location),
            country: str(entry.country),
            seniority: str(entry.seniority),
            businessUnit: str(entry.businessUnit),
            legalEntity: str(entry.legalEntity),
            salary: parseNumber(entry.salary),
            fte: parseNumber(entry.fte),
            levels,
            // Build-ups created before stages existed default to Day 1 — the same silent migration the app
            // performs on every load (orgchart.html:2049), not just on import.
            wave: entry.wave === 'day2' ? 'day2' : 'day1',
            tsaId: str(entry.tsaId),
            // 36 is the model's horizon — every timeline and every month input stops there — but the app
            // writes the field uncapped, so a long agreement's replacement date can arrive beyond it. The
            // clamp stays; going quiet about it does not, because the user can read the real month in the
            // file they just uploaded and would have no way to reconcile the two.
            neededBy: clampMonths(entry.neededBy, `build-up ${id}: needed-by`, report),
            day2Exit: !!entry.day2Exit,
            startMonth: clampMonths(entry.startMonth, `build-up ${id}: start`, report),
        });
        if (!parsed.success) {
            report.dropped.push(`build-up ${id}`);
            continue;
        }
        seen.add(id);
        out.push(parsed.data);
    }
    return out;
}
export function entitiesFromMeta(raw) {
    if (!Array.isArray(raw))
        return [];
    const out = [];
    const seenNames = new Set();
    for (const value of raw) {
        const entry = plainObject(value);
        if (!entry)
            continue;
        const name = str(entry.name);
        if (!name)
            continue;
        const lower = name.toLowerCase();
        if (seenNames.has(lower))
            continue;
        seenNames.add(lower);
        const id = str(entry.id) ?? `en_${lower.replace(/[^a-z0-9]+/g, '-')}`;
        if (isForbiddenKey(id))
            continue;
        const classification = entityClassificationSchema.safeParse(entry.classification);
        out.push({
            id,
            name,
            country: str(entry.country),
            // An unrecognised classification reads as retained, which is what an unregistered entity does.
            classification: classification.success ? classification.data : 'retain',
        });
    }
    return out;
}
export function targetDesignFromMeta(meta) {
    const levels = [];
    /*
      The key each level had IN THE FILE, alongside the key it gets here.
  
      Keys are positional on the platform, so they are regenerated rather than trusted — but the file's
      own keys are how its placements are addressed, and the app produces gapped ladders routinely: one
      click on a middle level's ✕ leaves `targetLevels` as `[tlvl1, tlvl3]` (orgchart.html:5972) and
      `nextTargetLevelKey` is max+1 (:1981), so gaps compound. Reading the placement map by the NEW key
      then found nothing at `tlvl2`, and `placementPath` stops at the first gap — so every person arrived
      placed only as deep as the first missing level, with nothing said.
    */
    const fileKeys = [];
    if (Array.isArray(meta.targetLevels)) {
        meta.targetLevels.forEach((value, i) => {
            const entry = plainObject(value);
            if (!entry)
                return;
            fileKeys.push(str(entry.key) ?? `tlvl${i + 1}`);
            levels.push({ key: `tlvl${i + 1}`, label: str(entry.label) ?? `L${i + 1}` });
        });
    }
    const units = [];
    const seen = new Set();
    if (Array.isArray(meta.targetUnits)) {
        for (const value of meta.targetUnits) {
            if (!Array.isArray(value))
                continue;
            const path = value.map((v) => str(v)).filter((v) => v !== null);
            if (!path.length || path.length !== value.length)
                continue; // a hole is not a unit
            const key = JSON.stringify(path);
            if (seen.has(key))
                continue;
            seen.add(key);
            units.push(path);
        }
    }
    const placements = Object.create(null);
    const overrides = overridesFromMeta(meta.targetOverrides);
    for (const [employeeId, fields] of Object.entries(overrides.target?.empOverrides ?? {})) {
        const byLevel = Object.create(null);
        levels.forEach((level, i) => {
            // Read by the file's key, stored under ours.
            const value = fields[fileKeys[i] ?? level.key];
            if (typeof value === 'string' && value.trim() !== '')
                byLevel[level.key] = value;
        });
        if (Object.keys(byLevel).length)
            placements[employeeId] = byLevel;
    }
    return { enabled: !!meta.targetEnabled, levels, units, placements };
}
/**
 * Defensive per-leaf overlay, mirroring mergeNodeDisplay (orgchart.html:9617).
 *
 * Per LEAF, and that is the whole point. This used to validate the merged object once at the end and
 * return the DEFAULTS wholesale on any failure — so a single value the app's editor produces and this
 * schema refuses discarded all thirty visibility toggles, the stage-line configuration and all seven
 * colours at once, silently, on the path users treat as lossless. The editor reaches such values
 * routinely: it stores `Math.max(0, Number(v) || 0)` with `min="0"` and no maximum (`:9686`, `:9724`)
 * against a `0.1`–`20` bound, and merges a colour without checking it is six hex digits (`:9627`).
 *
 * So a bad `box.bw` now costs `box.bw`, and says so in the report.
 */
export function nodeDisplayFromMeta(raw, defaults, report) {
    const source = plainObject(raw);
    if (!source)
        return null;
    const merged = JSON.parse(JSON.stringify(defaults));
    const drop = (what) => {
        report?.dropped.push(`node display: ${what}`);
    };
    for (const group of ['person', 'group', 'root', 'stageLine']) {
        const saved = plainObject(source[group]);
        if (!saved)
            continue;
        const target = merged[group];
        for (const key of Object.keys(target)) {
            if (typeof saved[key] === 'boolean')
                target[key] = saved[key];
            else if (key in saved)
                drop(`${group}.${key} is not a true/false value`);
        }
    }
    const colours = plainObject(source.colors);
    if (colours) {
        const target = merged.colors;
        const overlayColours = (savedGroup, targetGroup, path) => {
            if (!savedGroup)
                return;
            for (const key of Object.keys(targetGroup)) {
                const value = savedGroup[key];
                if (value === undefined)
                    continue;
                // Six hex digits, no leading '#'. The app merges whatever the picker produced.
                if (typeof value === 'string' && /^[0-9A-Fa-f]{6}$/.test(value))
                    targetGroup[key] = value;
                else
                    drop(`${path}${key} is not a six-digit hex colour`);
            }
        };
        overlayColours(plainObject(colours.box), target.box, 'colors.box.');
        overlayColours(plainObject(colours.status), target.status, 'colors.status.');
        if (colours.buildUp !== undefined) {
            if (typeof colours.buildUp === 'string' && /^[0-9A-Fa-f]{6}$/.test(colours.buildUp)) {
                target.buildUp = colours.buildUp;
            }
            else {
                drop('colors.buildUp is not a six-digit hex colour');
            }
        }
    }
    const box = plainObject(source.box);
    if (box) {
        const target = merged.box;
        for (const key of Object.keys(target)) {
            const value = box[key];
            if (value === undefined)
                continue;
            if (typeof value !== 'number' || !Number.isFinite(value)) {
                drop(`box.${key} is not a number`);
                continue;
            }
            // Clamped rather than dropped: a deck box the app let somebody type as 40 inches wide is a
            // legible intention (make it big) that the bound can honour, unlike a colour spelt "nope".
            const min = key === 'bw' || key === 'bh' ? 0.1 : 0;
            const clamped = Math.min(20, Math.max(min, value));
            if (clamped !== value)
                drop(`box.${key} ${value} clamped to ${clamped}`);
            target[key] = clamped;
        }
    }
    /*
      A final parse, as a backstop rather than as the filter.
  
      Every leaf above is already checked, so this can only fail if the schema gains a constraint the
      overlay does not know about — in which case falling back to the defaults is the honest answer, and
      it is now reported rather than silent.
    */
    const parsed = nodeDisplaySchema.safeParse(merged);
    if (parsed.success)
        return parsed.data;
    drop(`rejected wholesale: ${parsed.error.issues[0]?.message ?? 'invalid'}`);
    return defaults;
}
/**
 * Stricter than nodeDisplay by design: the original rejects a partial geometry wholesale (:9429).
 *
 * Kept wholesale, deliberately — a slide's title and content boxes are positioned relative to each
 * other, so half a geometry is not a usable geometry. But the rejection is REPORTED now: it used to
 * return null and let the caller substitute the defaults, which looks identical on screen to a file
 * that never carried a geometry at all.
 */
export function pptxGeomFromMeta(raw, report) {
    const source = plainObject(raw);
    if (!source)
        return null;
    if (!source.title || !source.content) {
        report?.dropped.push('slide geometry: incomplete — needs both a title and a content box');
        return null;
    }
    const parsed = pptxGeomSchema.safeParse(source);
    if (parsed.success)
        return parsed.data;
    report?.dropped.push(`slide geometry: ${parsed.error.issues[0]?.message ?? 'invalid'}`);
    return null;
}
export function filtersFromMeta(raw) {
    if (!Array.isArray(raw))
        return [];
    const out = [];
    for (const value of raw) {
        const parsed = filterConditionSchema.safeParse(value);
        if (parsed.success)
            out.push(parsed.data);
    }
    return out;
}
export function viewsFromMeta(meta) {
    if (!Array.isArray(meta.views))
        return [];
    const defaultViewId = str(meta.defaultViewId);
    const out = [];
    const seen = new Set();
    for (const value of meta.views) {
        const entry = plainObject(value);
        if (!entry)
            continue;
        const id = str(entry.id);
        if (!id || isForbiddenKey(id) || seen.has(id))
            continue;
        seen.add(id);
        const grouping = viewGroupingSchema.safeParse(entry.viewGrouping);
        out.push({
            id,
            name: str(entry.name) ?? id,
            viewGrouping: grouping.success ? grouping.data : null,
            filters: filtersFromMeta(entry.filters),
            viewLayout: entry.viewLayout === 'reporting' ? 'reporting' : 'grouping',
            isDefault: id === defaultViewId,
        });
    }
    return out;
}
/**
 * The perimeter rule, or null for no rule.
 *
 * The null check is not defensive tidiness. `Number(null)` is 0, and 0 is a perfectly valid threshold —
 * it means "every dedicated person" — so coercing first would turn a file with NO rule into a project
 * with a 0% rule that sweeps everyone carrying any dedication at all into the carve-out.
 */
function threshold(value) {
    if (value === null || value === undefined || value === '')
        return null;
    const n = Number(value);
    return Number.isFinite(n) && n >= 0 && n <= 1 ? n : null;
}
/**
 * Every scenario the file describes.
 *
 * A file written by a version that predates scenarios has its design at the top level, so that becomes
 * a single scenario. A file with a `scenarios` array is authoritative — which is exactly the trap that
 * caught the exporter in P2: the shipped reader loads the active scenario OVER whatever the rows
 * implied, so writing an empty array silently discarded the whole design.
 */
export function scenarioEntriesFromMeta(meta, known, groupingLevels, report) {
    const read = (source, id, name, isDefault) => {
        const overrides = overridesFromMeta(source.scenarioOverrides);
        const disabledLevels = Object.create(null);
        for (const bundle of Object.values(overrides)) {
            for (const levelKey of bundle.disabledLevels)
                disabledLevels[levelKey] = true;
        }
        // Build-ups are read FIRST, because a planned hire carries a bucket, a stage attribution and a
        // stranded plan like anybody else — so they have to be in the set of people a decision may name.
        const buildUps = buildUpsFromMeta(source.buildUps ?? meta.buildUps, groupingLevels, report);
        const decidable = new Set([...known, ...buildUps.map((b) => b.id)]);
        return {
            id,
            name,
            isDefault,
            perimeterThreshold: threshold(source.perimeterThreshold),
            disabledLevels,
            buckets: bucketsFromMeta(source.buckets, decidable, report),
            bucketMeta: bucketMetaFromMeta(source.bucketMeta, decidable),
            strandedPlan: strandedPlanFromMeta(source.strandedPlan, decidable),
            heads: headsFromMeta(source.heads),
            overrides,
            tsas: tsasFromMeta(source.tsas, report),
            buildUps,
        };
    };
    const raw = Array.isArray(meta.scenarios) ? meta.scenarios : [];
    const entries = [];
    const seen = new Set();
    const defaultId = str(meta.defaultScenarioId) ?? str(meta.activeScenarioId);
    for (const value of raw) {
        const entry = plainObject(value);
        if (!entry)
            continue;
        const id = str(entry.id);
        if (!id || isForbiddenKey(id) || seen.has(id))
            continue;
        seen.add(id);
        entries.push(read(entry, id, str(entry.name) ?? 'Scenario', id === defaultId));
    }
    if (entries.length) {
        // A file naming no default, or naming one that is not in the list, still has to open somewhere.
        if (!entries.some((e) => e.isDefault))
            entries[0].isDefault = true;
        return entries;
    }
    // Pre-scenario file: the top level IS the design.
    const name = str(meta.activeScenarioName) ?? 'Base case';
    return [read(meta, 's-imported-base', name, true)];
}
//# sourceMappingURL=import.js.map